import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, videos, articles, siteInfo } from "@/mocks/videoData";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import VideoCard from "./components/VideoCard";
import ArticleCard from "./components/ArticleCard";

export default function Home() {
  const [activeCategory, setActiveCategory] = useState("remen-tuijian");
  const [filteredVideos, setFilteredVideos] = useState(videos);

  useEffect(() => {
    if (activeCategory === "remen-tuijian") {
      setFilteredVideos([...videos].sort((a, b) => parseFloat(b.views.replace("万", "")) - parseFloat(a.views.replace("万", ""))));
    } else if (activeCategory === "zuixin-shangxian") {
      setFilteredVideos([...videos].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()));
    } else {
      setFilteredVideos(videos.filter((v) => v.category === activeCategory));
    }
  }, [activeCategory]);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection />

        {/* 分类导航 */}
        <section className="border-b border-background-200 bg-background-50 sticky top-16 z-40">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => {
                    setActiveCategory(cat.slug);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* 视频列表 */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                {categories.find((c) => c.slug === activeCategory)?.name || "视频资源"}
              </h2>
              <Link
                to={`/category/${activeCategory}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                查看更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-product-shop>
              {filteredVideos.slice(0, 10).map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* 平台特色 */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
              为什么选择我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: "ri-database-2-line", title: "海量资源", desc: "汇集久久视频、芒果视频、97视频等平台优质内容" },
                { icon: "ri-speed-up-line", title: "极速播放", desc: "多线路智能切换，告别卡顿，畅享流畅观影体验" },
                { icon: "ri-smartphone-line", title: "多端适配", desc: "电脑、手机、平板全设备覆盖，随时随地在线看视频" },
                { icon: "ri-thumb-up-line", title: "品质推荐", desc: "智能算法精准推荐，帮你发现更多喜欢的视频资源" },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-lg bg-background-50 border border-background-200">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 最新文章 */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">最新文章</h2>
              <Link
                to="/category/remen-tuijian"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                更多文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": `${siteInfo.url}/#organization`,
                name: siteInfo.name,
                url: siteInfo.url,
                description: siteInfo.description,
                email: siteInfo.email,
                telephone: siteInfo.phone,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: siteInfo.address,
                },
              },
              {
                "@type": "WebSite",
                "@id": `${siteInfo.url}/#website`,
                url: siteInfo.url,
                name: siteInfo.name,
                description: siteInfo.description,
                publisher: { "@id": `${siteInfo.url}/#organization` },
              },
              {
                "@type": "WebPage",
                "@id": `${siteInfo.url}/#webpage`,
                url: siteInfo.url,
                name: `首页 - ${siteInfo.name}`,
                isPartOf: { "@id": `${siteInfo.url}/#website` },
                about: { "@id": `${siteInfo.url}/#organization` },
              },
              {
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "国产91视频支持哪些视频播放器？",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "国产91视频兼容久久视频、芒果视频、97视频、丝瓜视频、蓝莓视频等多个平台的视频播放器，无需安装额外插件即可在线看视频。",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "如何找到想要的视频资源？",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "您可以通过首页的分类导航（电影、电视剧、综艺、动漫、短剧等）快速筛选视频，也可以使用搜索功能精准查找想要的视频资源。",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "是否需要注册才能使用？",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "国产91视频大部分功能无需注册即可使用，您可以直接浏览和观看视频资源。",
                    },
                  },
                ],
              },
            ],
          }),
        }}
      />
    </>
  );
}